import { supabase } from "./supabase";
import { dsHttpOrigin } from "../online/net";

// Admin-only: play stats from the host (edge-server stats/stats-api.js). Who is
// playing right now (the matches' heartbeats) and games and players per day
// (the play log). The live numbers count bots too, so none of this is for players.

export interface LiveSeats { matches: number; humans: number; guests: number; bots: number }
export interface LiveStats {
  at: number;
  playing: number;             // people in a match now
  total: LiveSeats;
  modes: Record<string, LiveSeats>;
  waiting: Record<string, number>; // people in a box that hasn't started (ranked search, the Royale's sign-up)
  waitingTotal: number;
  viewers: number;             // sockets on the start page and the room lists
}
export interface DailyRow { day: string; mode: string; games: number; rounds: number; players: number; signed_in: number; guests: number }

// The modes as the server names them (stats/play-log.js playModeOf), in page order.
export const MODE_LABELS: Record<string, string> = {
  playnow: "Play Now",
  royale: "Royale",
  "ranked-duel": "Ranked 1v1",
  "ranked-ffa": "Ranked 6 FFA",
  custom: "Game room",
  named: "Invite room",
  solo: "Instant match",
};
export const modeLabel = (mode: string) => MODE_LABELS[mode] || mode;

async function adminGet<T>(path: string, init?: RequestInit): Promise<T | { error: string }> {
  let token: string | null = null;
  try {
    const { data } = supabase ? await supabase.auth.getSession() : { data: { session: null } };
    token = data.session?.access_token ?? null;
  } catch { /* no session */ }
  if (!token) return { error: "not_signed_in" };
  try {
    const res = await fetch(`${dsHttpOrigin()}/api/admin/stats${path}`, { ...init, headers: { authorization: `Bearer ${token}` } });
    const body = await res.json();
    return res.ok ? body : { error: body.error || "failed" };
  } catch {
    return { error: "network" };
  }
}

export const fetchLiveStats = () => adminGet<LiveStats>("/live");

export interface ActivityRow { day: string; new_accounts: number; logins: number; new_players: number; returning_players: number }

function zone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
}
// withAdmins: count the admins' own accounts too (left out by default).
const q = (days: number, withAdmins: boolean) => `?days=${days}&tz=${encodeURIComponent(zone())}${withAdmins ? "&admins=1" : ""}`;

// admins: true when the rows include the admins (asked to, or the database is
// before migration 0017 and cannot leave them out).
export function fetchDailyStats(days: number, withAdmins = false): Promise<{ rows: DailyRow[]; unconfigured?: boolean; admins?: boolean } | { error: string }> {
  return adminGet(`/daily${q(days, withAdmins)}`);
}
export function fetchActivity(days: number, withAdmins = false): Promise<{ rows: ActivityRow[]; unconfigured?: boolean } | { error: string }> {
  return adminGet(`/activity${q(days, withAdmins)}`);
}

export const backfillFromReplays = () => adminGet<{ ok: boolean; rows: number; sent: number; more: boolean }>("/backfill", { method: "POST" });
